import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from 'framer-motion';
import { Camera } from 'lucide-react';
import { getMemberByUser } from '@/lib/memberLookup';
import { optimizeProfileImage } from '@/lib/imageCompression';
import ProfilePhotoCropper from '@/components/profile/ProfilePhotoCropper';

export default function EditarPerfil() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const photoInputRef = useRef(null);

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: memberData, isLoading } = useQuery({
    queryKey: ['memberData', currentUser?.id, currentUser?.email],
    queryFn: () => getMemberByUser(currentUser),
    enabled: !!currentUser
  });

  const [formData, setFormData] = useState({
    full_name: '',
    cpf: '',
    birth_date: '',
    phone: '',
    email: '',
    gender: '',
    marital_status: '',
    parent_name: '',
    parent_phone: '',
    cep: '',
    address: '',
    address_number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    congregation: '',
    membership_date: '',
    baptism_date: '',
    photo_url: ''
  });

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);

  useEffect(() => {
    if (memberData) {
      setFormData({
        full_name: memberData.full_name || '',
        cpf: memberData.cpf || '',
        birth_date: memberData.birth_date || '',
        phone: memberData.phone || '',
        email: memberData.email || '',
        gender: memberData.gender || '',
        marital_status: memberData.marital_status || '',
        parent_name: memberData.parent_name || '',
        parent_phone: memberData.parent_phone || '',
        cep: memberData.cep || '',
        address: memberData.address || '',
        address_number: memberData.address_number || '',
        complement: memberData.complement || '',
        neighborhood: memberData.neighborhood || '',
        city: memberData.city || '',
        state: memberData.state || '',
        congregation: memberData.congregation || '',
        membership_date: memberData.membership_date || '',
        baptism_date: memberData.baptism_date || '',
        photo_url: memberData.photo_url || ''
      });
    }
  }, [memberData]);

  const updateMutation = useMutation({
    mutationFn: (data) => base44.entities.Member.update(memberData.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberData'] });
      toast.success('Perfil atualizado com sucesso!');
      navigate(-1);
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.error('Erro ao atualizar perfil:', error);
      }
      toast.error(error?.message || 'Erro ao atualizar perfil.');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.phone || !formData.email) {
      toast.error('Preencha os campos obrigatórios.');
      return;
    }
    updateMutation.mutate({
      ...formData,
      email: String(formData.email || '').trim().toLowerCase(),
      user_id: memberData.user_id || currentUser?.id || undefined,
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedPhotoFile(file);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handleCroppedPhotoUpload = async (croppedFile) => {
    setUploadingPhoto(true);
    try {
      const optimizedFile = await optimizeProfileImage(croppedFile);
      const result = await base44.integrations.Core.UploadFile({ file: optimizedFile });
      setFormData((prev) => ({ ...prev, photo_url: result.file_url }));
      setSelectedPhotoFile(null);
      toast.success('Foto carregada com sucesso!');
    } catch (error) {
      toast.error(error?.message || 'Erro ao fazer upload da foto.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const formatCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1');
  };

  const fetchAddressByCEP = async (cep) => {
    const cleanCEP = cep.replace(/\D/g, '');
    if (cleanCEP.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setFormData({
          ...formData,
          address: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
          cep: formatCEP(cep)
        });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  const openPhotoPicker = () => {
    photoInputRef.current?.click();
  };

  if (isLoading || !memberData) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <ProfilePhotoCropper
        file={selectedPhotoFile}
        open={!!selectedPhotoFile}
        isProcessing={uploadingPhoto}
        onCancel={() => setSelectedPhotoFile(null)}
        onConfirm={handleCroppedPhotoUpload}
      />

      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Editar Perfil" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Foto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <Label className="mb-3 block">Foto de Perfil</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                {formData.photo_url ? (
                  <img src={formData.photo_url} alt="Foto" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
                <Button type="button" variant="outline" disabled={uploadingPhoto} onClick={openPhotoPicker}>
                  {uploadingPhoto ? 'Enviando...' : 'Alterar Foto'}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Dados Pessoais */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm space-y-4"
          >
            <h3 className="font-semibold text-lg text-gray-900 mb-4">Dados Pessoais</h3>
            
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CPF</Label>
                <Input
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: formatCPF(e.target.value) })}
                  maxLength={14}
                />
              </div>
              <div>
                <Label>Data de Nascimento</Label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                  maxLength={15}
                />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gênero</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Masculino">Masculino</SelectItem>
                    <SelectItem value="Feminino">Feminino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado Civil</Label>
                <Select 
                  value={formData.marital_status} 
                  onValueChange={(value) => setFormData({ ...formData, marital_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Solteiro(a)">Solteiro(a)</SelectItem>
                    <SelectItem value="Casado(a)">Casado(a)</SelectItem>
                    <SelectItem value="Divorciado(a)">Divorciado(a)</SelectItem>
                    <SelectItem value="Viúvo(a)">Viúvo(a)</SelectItem>
                    <SelectItem value="União Estável">União Estável</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Congregação</Label>
              <Select
                value={formData.congregation}
                onValueChange={(value) => setFormData({ ...formData, congregation: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione sua congregação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SEDE">SEDE</SelectItem>
                  <SelectItem value="PARQUE BRASÍLIA">PARQUE BRASÍLIA</SelectItem>
                  <SelectItem value="BAIRRO DE LOURDES">BAIRRO DE LOURDES</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Nome dos Pais/Responsável</Label>
              <Input
                value={formData.parent_name}
                onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
              />
            </div>

            <div>
              <Label>Telefone dos Pais/Responsável</Label>
              <Input
                value={formData.parent_phone}
                onChange={(e) => setFormData({ ...formData, parent_phone: formatPhone(e.target.value) })}
                maxLength={15}
              />
            </div>
          </motion.div>

          {/* Endereço */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm space-y-4"
          >
            <h3 className="font-semibold text-lg text-gray-900 mb-4">Endereço</h3>

            <div>
              <Label>CEP</Label>
              <Input
                value={formData.cep}
                onChange={(e) => setFormData({ ...formData, cep: formatCEP(e.target.value) })}
                onBlur={(e) => fetchAddressByCEP(e.target.value)}
                maxLength={9}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Endereço</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div>
                <Label>Número</Label>
                <Input
                  value={formData.address_number}
                  onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Complemento</Label>
              <Input
                value={formData.complement}
                onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bairro</Label>
                <Input
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Estado</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
          </motion.div>

          {/* Dados da Igreja */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm space-y-4"
          >
            <h3 className="font-semibold text-lg text-gray-900 mb-4">Dados da Igreja</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data de Membresia</Label>
                <Input
                  type="date"
                  value={formData.membership_date}
                  onChange={(e) => setFormData({ ...formData, membership_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Data de Batismo</Label>
                <Input
                  type="date"
                  value={formData.baptism_date}
                  onChange={(e) => setFormData({ ...formData, baptism_date: e.target.value })}
                />
              </div>
            </div>
          </motion.div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
